import { Children, type ReactNode } from 'react';

import type { FormParameters } from '@tmh-website/services/form.service';
import { ContactSupport } from '@tmh-website/shared/cms';
import { Color } from '@tmh-website/shared/colors';
import type { LocalePrefix } from '@tmh-website/shared/const';
import StyledTypo from '@tmh-website/styles/typo';

import Form from '../Form';

import * as Styled from './ContactCard.styles';
import type { ContactCardThemeColor } from './ContactCard.theme';

interface ContactCardProps {
  data: ContactSupport['contactSupportCategories'][number];
  themeColor: ContactCardThemeColor;
  locale: LocalePrefix;
  currentPath: string;
  children: ReactNode;
}

const ContactCard = ({ data, themeColor, locale, currentPath, children, ...props }: ContactCardProps) => {
  const hasForm = Object.keys((data.formParameters as FormParameters) ?? {}).length > 0;
  const displayContact = data.displayContact;

  const renderTopBar = (headline?: string): JSX.Element | null => {
    if (!headline) {
      return null;
    }
    return (
      <Styled.TopBar $themeColor={Color.Neutral_200_OffWhite}>
        <StyledTypo.Typography $style='uiTextSmall' as='span'>
          {headline}
        </StyledTypo.Typography>
      </Styled.TopBar>
    );
  };

  if (!hasForm && !displayContact && Children.count(children) === 0) {
    return null;
  }
  return (
    <Styled.ContactCard $hasForm={hasForm} $hasContact={displayContact}>
      {/* Form section & FAQ section to make sure the width are identical */}
      {(Children.count(children) > 0 || hasForm) && (
        <Styled.Container $themeColor={themeColor} $display='form' {...props}>
          {hasForm && (
            <>
              {renderTopBar(data.contactCardHeadlineForForm)}
              <Styled.BottomBar $themeColor={themeColor} $display='form'>
                <Form
                  data={{
                    __component: 'content.form',
                    alignment: 'center',
                    backgroundColor: Color.Neutral_200_OffWhite,
                    formParameters: data.formParameters as FormParameters,
                    hasPaddingBottom: false,
                    hasPaddingTop: false,
                    id: data.id,
                    isVisible: true,
                  }}
                  locale={locale}
                  currentPath={currentPath}
                />
              </Styled.BottomBar>
            </>
          )}
          {children}
        </Styled.Container>
      )}

      {/* contact area */}
      {hasForm && displayContact && (
        <Styled.Container $themeColor={themeColor} $display='contact' {...props}>
          {renderTopBar(data.contactCardHeadlineForContact)}
          <Styled.BottomBar $themeColor={themeColor} $display='contact'>
            {data.contactSupportInfoEntries!.slice(0, 2).map((entry, index) => (
              <div key={index}>
                {entry.textTop && (
                  <StyledTypo.Typography $style='h6' as='p' $noMargin>
                    {entry.textTop}
                  </StyledTypo.Typography>
                )}
                {entry.phone && (
                  <Styled.TelLink
                    href={`tel:${entry.phone}`}
                    $style='uiTextBold'
                    as='a'
                    $noMargin
                    $themeColor={themeColor}>
                    {entry.phone}
                  </Styled.TelLink>
                )}
                {entry.textBottom && (
                  <StyledTypo.Typography $style='h6' as='p' $noMargin>
                    {entry.textBottom}
                  </StyledTypo.Typography>
                )}
              </div>
            ))}
          </Styled.BottomBar>
        </Styled.Container>
      )}
    </Styled.ContactCard>
  );
};

export default ContactCard;
